// ── errorLog.js — журнал ошибок приложения ───────────────────────────────────
// Ошибки на сайте, в телефоне и в программе для компьютера записываются в таблицу
// client_errors (supabase_migration_client_errors.sql). Админ видит их в
// админ-панели → «Ошибки» и копирует одним нажатием.
//
// Пишем: необработанные ошибки, отклонённые промисы, падения React и console.error.
// Одинаковые сообщения за сеанс уходят один раз, всего за сеанс — не больше 20.
// Без сети ошибки ждут в localStorage и уходят при следующем подключении.
import { supabase } from './supabase';
import { isNative, isElectron } from './utils';

const BUILD_ID = process.env.REACT_APP_BUILD_ID || 'dev';
const QUEUE_KEY = 'ac_error_queue';
const MAX_PER_SESSION = 20;
const MAX_QUEUE = 30;
// Шум, по которому ничего не исправить.
const IGNORE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /AbortError|aborted a request|signal is aborted/i,
  /The play\(\) request was interrupted/i,
  // Штатные ответы плагинов телефона (проверка «есть ли файл», повторное подключение к базе)
  /duplicate column name/i,
  /Connection \w+ already exists/i,
  /'stat' failed because file .* does not exist/i,
  // После выхода или «выкинуть» сервер уже забыл сеанс — это ожидаемо.
  /Invalid Refresh Token/i,
];

const seen = new Set();
let sent = 0;
let disabled = false; // таблицы нет (SQL ещё не запускали) — не пытаемся до перезапуска
let inReport = false;

const platform = () => (isNative() ? 'android' : isElectron() ? 'desktop' : 'web');

const readQueue = () => { try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; } };
const writeQueue = (q) => { try { q.length ? localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE))) : localStorage.removeItem(QUEUE_KEY); } catch {} };

function describe(value) {
  if (value instanceof Error) return { message: `${value.name}: ${value.message}`, stack: value.stack || '' };
  if (typeof value === 'string') return { message: value, stack: '' };
  try { return { message: JSON.stringify(value), stack: '' }; } catch { return { message: String(value), stack: '' }; }
}

async function send(rows) {
  const { error } = await supabase.from('client_errors').insert(rows);
  if (!error) return true;
  if (/client_errors|does not exist|PGRST205|42P01/i.test(`${error.code} ${error.message}`)) disabled = true;
  return false;
}

export function reportError(kind, value, extraStack = '') {
  if (disabled || inReport || sent >= MAX_PER_SESSION) return;
  inReport = true;
  try {
    const { message, stack } = describe(value);
    if (!message || IGNORE.some((re) => re.test(message))) return;
    // Одно и то же сообщение — один раз, как бы его ни поймали (падение React потом
    // ещё и печатается в консоль).
    if (seen.has(message)) return;
    seen.add(message);
    sent++;
    const row = {
      platform: platform(),
      app_version: BUILD_ID,
      kind,
      message: message.slice(0, 1000),
      stack: `${stack}${extraStack ? `\n${extraStack}` : ''}`.slice(0, 4000),
      // Без параметров после «?»: там бывает поисковый запрос.
      url: `${window.location.pathname}${window.location.hash.split('?')[0]}`.slice(0, 300),
      user_agent: navigator.userAgent.slice(0, 300),
    };
    const queue = () => writeQueue([...readQueue(), { ...row, at: new Date().toISOString() }]);
    if (navigator.onLine === false) { queue(); return; }
    send([row]).then((ok) => { if (!ok && !disabled) queue(); }, queue);
  } finally {
    inReport = false;
  }
}

async function flushQueue() {
  const q = readQueue();
  if (!q.length || disabled || navigator.onLine === false) return;
  // Время ошибки сохраняем в сообщении: created_at будет временем отправки.
  const rows = q.map(({ at, ...row }) => ({ ...row, message: `${row.message} (было ${at})`.slice(0, 1000) }));
  if (await send(rows).catch(() => false)) writeQueue([]);
}

export function startErrorLog() {
  window.addEventListener('error', (e) => reportError('error', e.error || e.message));
  window.addEventListener('unhandledrejection', (e) => reportError('promise', e.reason));
  const original = console.error.bind(console);
  console.error = (...args) => {
    original(...args);
    const first = args.find((a) => a instanceof Error) || args.map((a) => describe(a).message).join(' ');
    reportError('console', first);
  };
  window.addEventListener('online', flushQueue);
  setTimeout(flushQueue, 10000);
}
